import { useEffect, useState } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { ApiScenarioResponse } from '@/shared/api/generated/data-contracts';
import { useApi } from '@/shared/api/hooks/useApi';
import { useApiMyhub } from '@/shared/apiMyhub/useApiMyhub';

import { useScenariosStore } from '../scenarios.store';

/**
 * Automatically updates cache after success.
 *
 * Cache save time unlimited, updates on each session
 * */
const useFetchScenario = () => {
  const api = useApi();
  const apiMyhub = useApiMyhub();
  const { individualScenariosMap, updateScenario } = useScenariosStore();

  const [loading, setIsLoading] = useState(false);

  const getScenarioData = async ({
    scenarioDbId,
    cached,
  }: {
    scenarioDbId: string;
    cached?: boolean;
  }): Promise<ApiScenarioResponse | undefined> => {
    let scenarioData: ApiScenarioResponse | undefined;

    if (clientConfig.isExported) {
      if (clientConfig.demoHub.isDemoHub) {
        const res = await apiMyhub.getScenarioList();
        if (res) {
          scenarioData = res?.find((s) => s.id === scenarioDbId);
        }
      } else {
        throw new Error('Myhub doesnt support scenarios');
      }
    } else {
      const cachedData = individualScenariosMap[scenarioDbId];

      if (cached && cachedData) {
        return cachedData;
      }

      setIsLoading(true);
      const { data } = await api.getScenario(scenarioDbId);
      setIsLoading(false);

      if (data) {
        scenarioData = data;
      }
    }

    if (scenarioData) {
      updateScenario(scenarioData);
    }

    return scenarioData;
  };

  return { getScenarioData, loading };
};

/**
 * Automatically updates cache after success.
 *
 * Cache save time unlimited, updates on each session
 * */
export const useScenario = ({
  scenarioDbId,
  cached,
}: {
  scenarioDbId: string;
  cached?: boolean;
}) => {
  const { individualScenariosMap } = useScenariosStore();
  const { getScenarioData, loading } = useFetchScenario();

  const scenario = individualScenariosMap[scenarioDbId];

  const refetch = async (): Promise<ApiScenarioResponse | undefined> => {
    const scenarioData = await getScenarioData({ scenarioDbId, cached: false });

    return scenarioData;
  };

  useEffect(() => {
    getScenarioData({ scenarioDbId, cached });
  }, []);

  return { refetch, scenario, loading };
};

/**
 * Automatically updates cache after success.
 *
 * Cache save time unlimited, updates on each session
 * */
export const useScenarioLazy = () => {
  const { getScenarioData, loading } = useFetchScenario();
  const { individualScenariosMap } = useScenariosStore();
  const [scenarioDbId, setScenarioDbId] = useState<string>();

  const scenario = scenarioDbId ? individualScenariosMap[scenarioDbId] : undefined;

  const getScenario = async ({
    cached,
    scenarioDbId: _scenarioDbId,
  }: {
    scenarioDbId: string;
    cached?: boolean;
  }): Promise<ApiScenarioResponse | undefined> => {
    setScenarioDbId(scenarioDbId);
    const scenarioData = await getScenarioData({ scenarioDbId: _scenarioDbId, cached });

    return scenarioData;
  };

  return { getScenario, scenario, loading };
};
